import { useEffect, useRef } from "react";
import type { Answer } from "../../../components/Questionnaire/Section/Question/types";
import { useAnswer } from "./useAnswer";
import { useQuestionnaire } from "./useQuestionnaire";

const PROPERTY_TYPE_QUESTION = "Property type";

/**
 * Builds the redirect URL for navigating from Commercial Landlord to Q2Q Landlord questionnaire.
 * Preserves existing query parameters (e.g., AFFCLIE, utm params).
 */
export const buildCommercialLandlordRedirectUrl = (search: string): string => {
  const baseUrl = "/landlord/questionnaire/redirect_from_cms";
  const params = new URLSearchParams({ landlord_type: "residential" });

  const existingParams = new URLSearchParams(search);
  existingParams.forEach((value, key) => {
    params.append(key, value);
  });

  return `${baseUrl}?${params.toString()}`;
};

export const shouldRedirectCommercialLandlord = ({
  isCommercialLandlordQuestionnaire,
  propertyType,
  hasRedirected,
}: {
  isCommercialLandlordQuestionnaire: boolean;
  propertyType?: Answer;
  hasRedirected: boolean;
}): boolean =>
  isCommercialLandlordQuestionnaire &&
  propertyType === "residential" &&
  !hasRedirected;

/**
 * Redirects users from Commercial Landlord to Q2Q Landlord questionnaire when they select
 * "residential" for Property type. The redirect happens immediately on selection
 * since the remaining questions on Commercial Landlord aren't relevant for residential.
 *
 * Uses direct URL redirect to /landlord/questionnaire/redirect_from_cms which routes
 * to the Quick to Quote (Q2Q) landlord journey (same pattern as non-L4L commercial landlord).
 *
 * This hook should be used in questionnaire pages that render the Commercial Landlord flow.
 */
export const useCommercialLandlordRedirect = () => {
  const { config } = useQuestionnaire();
  const propertyType = useAnswer(PROPERTY_TYPE_QUESTION);
  const hasRedirected = useRef(false);

  const isCommercialLandlordQuestionnaire =
    config?.vertical === "commercial_landlord";

  useEffect(() => {
    if (
      shouldRedirectCommercialLandlord({
        isCommercialLandlordQuestionnaire,
        propertyType,
        hasRedirected: hasRedirected.current,
      })
    ) {
      hasRedirected.current = true;
      window.location.assign(
        buildCommercialLandlordRedirectUrl(window.location.search),
      );
    }
  }, [isCommercialLandlordQuestionnaire, propertyType]);
};
